import { deepClone } from '@/helpers/objects_arrays'
import { ref, type Ref } from 'vue'

import { Logger, LoggerStyle } from '@/helpers/logger'

// Form value types - using unknown for flexibility with validation
type FormValue = unknown

interface FormObject {
  [key: string]: FormValue
}

type NestedObject = FormObject

// Validator function type
type ValidatorFn = (value: FormValue) => boolean | string

export type ValidationSchema = Record<string, ValidatorFn | ValidatorFn[]>

type Diff = {
  added: Record<string, FormValue>
  updated: Record<string, FormValue>
  deleted: Record<string, FormValue>
}

export class Validator {
  isDirty: boolean = false
  errors: Ref<Record<string, string[]>> = ref({})
  formValues: NestedObject = {}
  silentErrors: Record<string, string[]> = {}
  private logger: Logger = new Logger()
  originValues: NestedObject | null = null

  constructor(
    private debug: boolean = false,
    private initialized: boolean = false,
    private resetErrorsOnNextUpdate = false,
    private validationSchema: ValidationSchema = {}
  ) {
    if (this.debug) {
      this.logger = new Logger(this.debug)
    }
  }

  initForm(initialValues: NestedObject, validationSchema: ValidationSchema): void {
    this.reset()
    this.originValues = deepClone(initialValues)
    this.formValues = deepClone(initialValues)
    this.validationSchema = deepClone(validationSchema)
    this.initialized = true
  }

  reset(): void {
    this.isDirty = false
    this.errors.value = {}
    this.initialized = false
    this.validationSchema = {}
  }

  updateValidationSchema(validationSchema: ValidationSchema): void {
    this.validationSchema = validationSchema
  }

  getErrors(field: string): boolean | string[] {
    if (this.errors.value[field]) {
      return this.errors.value[field]
    }
    return false
  }

  private transformKey(key: string): string {
    let keyWithArraySyntax: string = key.replace(/\.+\d+\./g, '[].')
    keyWithArraySyntax = keyWithArraySyntax.replace(/\.+\d+$/g, '[]')
    return keyWithArraySyntax
  }

  onFormValuesChange(formValues: NestedObject): void {
    if (!this.initialized) {
      return
    }
    if (this.resetErrorsOnNextUpdate) {
      this.resetErrorsOnNextUpdate = false
      this.errors.value = {}
      this.silentErrors = {}
    }

    this.logger.group('Form Update', false, LoggerStyle.DEFAULT)
    // diff since the last change
    const diff = this.objectDiff(this.formValues, formValues)

    // diff to the initial value
    const diffToOrigin = this.objectDiff(this.originValues, formValues)

    // set dirty if any value has changed (compared with the origin values)
    this.isDirty =
      Object.values(diffToOrigin.updated).length > 0 ||
      Object.values(diffToOrigin.deleted).length > 0 ||
      Object.values(diffToOrigin.added).length > 0

    const updateKeys = Object.keys(diff.updated)
    updateKeys.forEach((key: string) => {
      const keyWithArraySyntax: string = this.transformKey(key)
      if (this.validationSchema[keyWithArraySyntax] !== undefined) {
        this.validateField(key, diff.updated[key], false, this.validationSchema[keyWithArraySyntax])
      }
    })

    const addedKeys = Object.keys(diff.added)
    addedKeys.forEach((key: string) => {
      const keyWithArraySyntax: string = this.transformKey(key)
      if (this.validationSchema[keyWithArraySyntax] !== undefined) {
        // values that do no exist yet, should not be silently validated
        const previousValue = this.get(this.formValues, key)
        const silentValidate = previousValue === null
        this.validateField(key, diff.added[key], silentValidate, this.validationSchema[keyWithArraySyntax])
      }
    })
    this.formValues = deepClone(formValues)
    this.logger.end()
  }

  private validateField(
    fieldPath: string,
    value: FormValue,
    silent: boolean,
    validator: ValidatorFn | ValidatorFn[]
  ): boolean {
    let result: boolean = true
    const errors: string[] = []
    let validators: ValidatorFn[]
    if (validator && !Array.isArray(validator)) {
      validators = [validator]
    } else {
      validators = validator as ValidatorFn[]
    }
    validators.forEach((v: ValidatorFn) => {
      const validationResult = v(value)
      if (validationResult !== true) {
        errors.push(validationResult as string)
        result = false
      }
    })
    if (result) {
      this.logger.group(`${fieldPath} [silent=${silent}, result=${result}]`, true, LoggerStyle.SUCCESS)
    } else {
      this.logger.group(`${fieldPath} [silent=${silent}, result=${result}]`, false, LoggerStyle.ERROR)
    }
    this.logger.value(value)
    if (errors.length) {
      this.logger.group('errors', false, LoggerStyle.ERROR)
      errors.forEach((error: string) => this.logger.error(error))
      this.logger.end()
    }
    this.logger.end()
    if (!result) {
      if (!silent) {
        this.errors.value[fieldPath] = errors.filter(unique)
      } else {
        delete this.errors.value[fieldPath]
      }
      this.silentErrors[fieldPath] = errors.filter(unique)
      return false
    }
    delete this.errors.value[fieldPath]
    delete this.silentErrors[fieldPath]
    return true
  }

  private doValidate(silent: boolean = false): boolean {
    let valid = true
    this.logger.group('Validate', false, LoggerStyle.DEFAULT)
    const formValuePaths = this.objectPaths(this.formValues)
    formValuePaths.forEach((path: string) => {
      const pathWithArraySyntax: string = this.transformKey(path)
      if (this.validationSchema[pathWithArraySyntax] !== undefined) {
        const formValue = this.get(this.formValues, path)
        const result = this.validateField(path, formValue, silent, this.validationSchema[pathWithArraySyntax])
        if (!result) {
          valid = false
        }
      }
    })
    this.logger.end()
    return valid
  }

  validate(): boolean {
    return this.doValidate()
  }

  silentValidate(): boolean {
    return this.doValidate(true)
  }

  isValid(): boolean {
    return Object.keys(this.silentErrors).length === 0
  }

  getValue(fieldPath: string): FormValue {
    const matches = fieldPath.match(/\.(\d)\./)
    if (matches) {
      const arrayPath = fieldPath.split(matches[0])
      if (arrayPath.length > 1) {
        const arrayValue = this.get(this.formValues, arrayPath[0])
        if (Array.isArray(arrayValue) && arrayValue.length >= Number(matches[1])) {
          const itemValue = arrayValue[Number(matches[1])]
          if (itemValue && typeof itemValue === 'object' && !Array.isArray(itemValue)) {
            const key = arrayPath[1].replace('.', '')
            return (itemValue as FormObject)[key]
          }
        }
      }
      return undefined
    }
    return this.get(this.formValues, fieldPath)
  }

  // noinspection JSMethodCanBeStatic
  private get(object: FormObject | null, path: string): FormValue {
    const keys = path.split('.')
    let result: unknown = object
    for (const key of keys) {
      if (result === null || result === undefined) {
        break
      }
      if (typeof result === 'object' && !Array.isArray(result)) {
        const obj = result as Record<string, unknown>
        const nextValue: unknown = obj[key]
        if (nextValue === undefined) {
          return nextValue
        }
        result = nextValue
      } else {
        return undefined
      }
    }
    return result
  }

  // noinspection JSMethodCanBeStatic
  private hasDiff(diff: Diff): boolean {
    return !!Object.keys(diff.added).length || !!Object.keys(diff.deleted).length || !!Object.keys(diff.updated).length
  }

  // noinspection JSMethodCanBeStatic
  private isArrayOrObject(obj: unknown): obj is Record<string, unknown> | unknown[] {
    return obj !== null && obj !== undefined && typeof obj === 'object'
  }

  // noinspection JSMethodCanBeStatic
  private isArray(obj: unknown): obj is unknown[] {
    return Array.isArray(obj)
  }

  private isObject(obj: unknown): obj is Record<string, unknown> {
    return obj !== null && obj !== undefined && typeof obj === 'object' && !Array.isArray(obj)
  }

  // noinspection JSMethodCanBeStatic
  private objectKeys(obj: Record<string, unknown> | unknown[]): string[] {
    return Object.keys(obj)
  }

  private objectPaths(obj: FormValue, path: string = '', paths: string[] = []): string[] {
    if (this.isArrayOrObject(obj)) {
      const keys = this.objectKeys(obj)
      if (keys.length) {
        keys.forEach((key: string) => {
          let p = key
          if (path !== '') {
            p = `${path}.${key}`
          }
          const value = (obj as Record<string, FormValue>)[key]
          if (!this.isArrayOrObject(value)) {
            paths.push(p)
          }
          this.objectPaths(value, p, paths)
        })
      }
    }
    paths.push(path)
    return paths.filter((p: string) => p !== '').filter(unique)
  }

  private objectDiff(original: FormObject | null, updated: FormObject): Diff {
    const diff: Diff = { added: {}, deleted: {}, updated: {} }
    const originalPaths = this.objectPaths(original)
    const updatedPaths = this.objectPaths(updated, '', [])

    this.logger.group('Info', true, LoggerStyle.DEFAULT)
    this.logger.group('original', true)
    this.logger.value(original)
    this.logger.value(originalPaths)
    this.logger.end()

    this.logger.group('updated', true)
    this.logger.value(updated)
    this.logger.value(updatedPaths)
    this.logger.end()

    originalPaths.forEach((path: string) => {
      if (!updatedPaths.includes(path)) {
        diff.deleted[path] = this.get(updated, path) ?? 'undefined'
      }
    })

    updatedPaths.forEach((path: string) => {
      if (!originalPaths.includes(path)) {
        diff.added[path] = this.get(updated, path)
      } else {
        const updatedValue = this.get(updated, path)
        const originalValue = this.get(original, path)
        if (this.isArray(updatedValue) && this.isArray(originalValue)) {
          if (updatedValue.length !== originalValue.length) {
            diff.updated[path] = updatedValue
          }
        } else if (!this.isObject(updatedValue) && updatedValue !== originalValue) {
          diff.updated[path] = updatedValue
        }
      }
    })

    if (this.hasDiff(diff)) {
      this.logger.group('diff', true, LoggerStyle.DEFAULT)
      this.logger.value(diff)
      this.logger.end()
    }
    this.logger.end()

    return diff
  }

  getDebug(): boolean {
    return this.debug
  }
}
const validator: Validator = new Validator()
export const useValidator = () => validator

const unique = (value: string, index: number, self: string[]): boolean => {
  return self.indexOf(value) === index
}
